import { ChangeDetectionStrategy, Component, input, Input, output, TrackByFunction } from "@angular/core";
import { WorkspaceSelected } from "common/workspace/workspace-selection.component";
import { NgClass } from "@angular/common";
import { ClickStopPropagationDirective } from "common/click-stop-propagation.directive";
import { TranslateModule } from "@ngx-translate/core";
import { SpinComponent } from "common/spin/spin.component";
import { HighlightMatchesPipe } from "common/collection/highlight-matches.pipe";

type Workspace = stages.workspace.Workspace;
type SlidePosition = "left" | "center";

@Component({
	selector: "stages-workspace-carousel",
	styleUrls: ["./workspace-carousel.component.scss"],
	templateUrl: "./workspace-carousel.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ClickStopPropagationDirective, NgClass, TranslateModule, SpinComponent, HighlightMatchesPipe],
})
export class WorkspaceCarouselComponent {
	private _folder!: stages.workspace.Folder;
	private _filterTerm?: string;
	private slideFrameId?: number;

	currentWorkspaceId!: string;

	visibleFolders: stages.workspace.Folder[] = [];
	slidePosition: SlidePosition = "center";
	transitionEnabled = false;

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input()
	set folder(folder: stages.workspace.Folder) {
		if (!folder) {
			return;
		}

		if (!this._folder) {
			this._folder = folder;
			this.visibleFolders = [folder];
			this.slidePosition = "center";
			this.transitionEnabled = false;
			return;
		}

		if (this._folder.id === folder.id) {
			return;
		}

		const previousFolder = this._folder;
		this._folder = folder;

		if (this.slideFrameId !== undefined) {
			cancelAnimationFrame(this.slideFrameId);
			this.slideFrameId = undefined;
		}

		this.transitionEnabled = false;

		if (previousFolder.parent && previousFolder.parent.id === folder.id) {
			// Navigate up:
			// Render [new parent, old child].
			// Start with old child visible on the right, then slide to the new parent on the left.
			this.visibleFolders = [folder, previousFolder];
			this.slidePosition = "left";

			this.slideFrameId = requestAnimationFrame(() => {
				this.slideFrameId = requestAnimationFrame(() => {
					this.transitionEnabled = true;
					this.slidePosition = "center";
				});
			});
		} else {
			// Navigate down:
			// Render [old parent, new child].
			// Start with old parent visible, then slide to the new child on the right.
			this.visibleFolders = [previousFolder, folder];
			this.slidePosition = "center";

			this.slideFrameId = requestAnimationFrame(() => {
				this.slideFrameId = requestAnimationFrame(() => {
					this.transitionEnabled = true;
					this.slidePosition = "left";
				});
			});
		}
	}

	get folder(): stages.workspace.Folder {
		return this._folder;
	}

	readonly preSelected = input<string>();

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input()
	set filterTerm(filterTerm: string | undefined) {
		this._filterTerm = filterTerm ? filterTerm.toLocaleLowerCase() : undefined;
	}

	get filterTerm(): string | undefined {
		return this._filterTerm;
	}

	readonly up = output<string>();

	readonly down = output<string>();

	readonly selected = output<WorkspaceSelected>();

	onSlideTransitionEnd(event: TransitionEvent): void {
		if (event.propertyName !== "transform") {
			return;
		}

		this.visibleFolders = [this._folder];
		this.slidePosition = "center";
		this.transitionEnabled = false;
	}

	onDown(folderEntry: stages.workspace.FolderEntry): void {
		this.down.emit(folderEntry.id);
	}

	onUp(folderEntry: stages.workspace.FolderEntry): void {
		this.up.emit(folderEntry.id);
	}

	selectWorkspace(workspace: stages.workspace.Node | Workspace): void {
		this.selected.emit(workspace as WorkspaceSelected);
	}

	getId: TrackByFunction<stages.workspace.FolderEntry> = (_index, folder) => {
		return folder.id;
	};

	getFolderId: TrackByFunction<stages.workspace.Folder> = (_index, folder) => {
		return folder.id;
	};

	filterChildren(children: stages.workspace.FolderEntry[]): stages.workspace.FolderEntry[] {
		return children.filter((child) => {
			return !this.filterTerm || child.name.toLocaleLowerCase().indexOf(this.filterTerm) >= 0;
		});
	}
}